import {
  Autocomplete,
  Box,
  Button,
  TextField,
  type AutocompleteInputChangeReason,
  type AutocompleteRenderInputParams,
  type SxProps,
  type Theme,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useCompaniesQuery,
  useTenediConnectionCustomersQuery,
  type CompaniesQuery,
  type TenediConnectionCustomersQuery,
} from "../../../generated/graphql";

type CarrierOption = CompaniesQuery["companies"][number];

export type CustomerOption = Pick<
  TenediConnectionCustomersQuery["tenediConnectionCustomers"][number],
  "uuid" | "displayName"
> &
  Partial<TenediConnectionCustomersQuery["tenediConnectionCustomers"][number]>;

const styles = {
  row: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: 2,
  } satisfies SxProps<Theme>,
  retry: { ml: 1, py: 0 } satisfies SxProps<Theme>,
};

/** The pause after the last keystroke before the customer picker searches. */
const SEARCH_DEBOUNCE_MS = 300;

const EMPTY_CARRIERS: CarrierOption[] = [];
const EMPTY_CUSTOMERS: CustomerOption[] = [];

const carrierLabel = (option: CarrierOption) => option.name;

/** The server already filtered and ordered the customers; MUI must not filter again by label. */
const keepServerResults = (options: CustomerOption[]) => options;
const isSameUuid = (
  option: { uuid: string },
  value: { uuid: string },
): boolean => option.uuid === value.uuid;

/** "Acme Freight · ZZFWD · 1 partnership", or less when the server sent less. */
export const customerOptionLabel = (option: CustomerOption): string => {
  const parts = [option.displayName];
  if (option.isaId !== null && option.isaId !== undefined) {
    parts.push(option.isaId);
  }
  if (option.partnershipCount !== undefined) {
    parts.push(
      option.partnershipCount === 1
        ? "1 partnership"
        : `${option.partnershipCount} partnerships`,
    );
  }
  if (option.isActive === false) {
    parts.push("inactive");
  }
  return parts.join(" · ");
};

type ConnectionPickersProps = {
  readonly carrierUuid: string | null;
  readonly customer: CustomerOption | null;
  readonly onCarrierChange: (uuid: string | null) => void;
  readonly onCustomerChange: (uuid: string | null) => void;
};

/**
 * Picks the carrier (any company, through the staff `companies` query) and one
 * of its customers (the staff `tenediConnectionCustomers` search).
 */
const ConnectionPickers = ({
  carrierUuid,
  customer,
  onCarrierChange,
  onCustomerChange,
}: ConnectionPickersProps) => {
  const [typed, setTyped] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(typed.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [typed]);

  // The internal app's watch queries default to `errorPolicy: "ignore"`,
  // which would show a failed load as an empty list.
  const carriers = useCompaniesQuery({ errorPolicy: "none" });
  const customers = useTenediConnectionCustomersQuery({
    errorPolicy: "none",
    skip: carrierUuid === null,
    variables: {
      input: {
        companyUuid: carrierUuid ?? "",
        search: search === "" ? null : search,
      },
    },
  });

  const carrierOptions = carriers.data?.companies ?? EMPTY_CARRIERS;
  const refetchCarriers = carriers.refetch;
  const carrier = useMemo(
    () => carrierOptions.find((option) => option.uuid === carrierUuid) ?? null,
    [carrierOptions, carrierUuid],
  );
  // Keeps the picked customer in the list, so a new search that no longer
  // returns it does not blank the field.
  const customerOptions = useMemo(() => {
    const results =
      customers.data?.tenediConnectionCustomers ?? EMPTY_CUSTOMERS;
    return customer === null ||
      results.some((option) => option.uuid === customer.uuid)
      ? results
      : [customer, ...results];
  }, [customer, customers.data]);

  const handleCarrierChange = useCallback(
    (_: unknown, value: CarrierOption | null) => {
      onCarrierChange(value?.uuid ?? null);
    },
    [onCarrierChange],
  );
  const handleCustomerChange = useCallback(
    (_: unknown, value: CustomerOption | null) => {
      onCustomerChange(value?.uuid ?? null);
    },
    [onCustomerChange],
  );
  const handleCustomerInput = useCallback(
    (_: unknown, value: string, reason: AutocompleteInputChangeReason) => {
      setTyped(reason === "input" ? value : "");
    },
    [],
  );

  const handleRetryCarriers = useCallback(() => {
    refetchCarriers().catch(() => {
      // The failed refetch sets `carriers.error`, which the field shows.
    });
  }, [refetchCarriers]);

  const carrierError = carriers.error?.message;
  const renderCarrierInput = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <TextField
        {...params}
        error={carrierError !== undefined}
        helperText={
          carrierError === undefined ? (
            "The trucking company that receives the tenders."
          ) : (
            <>
              Could not load carriers: {carrierError}
              <Button
                size="small"
                sx={styles.retry}
                onClick={handleRetryCarriers}
              >
                Retry
              </Button>
            </>
          )
        }
        label="Carrier"
      />
    ),
    [carrierError, handleRetryCarriers],
  );

  const customerError = customers.error?.message;
  const renderCustomerInput = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <TextField
        {...params}
        error={customerError !== undefined}
        helperText={
          customerError === undefined
            ? "The forwarder, as a customer of this carrier. Search by name or EDI ID."
            : `Could not load customers: ${customerError}`
        }
        label="Customer"
      />
    ),
    [customerError],
  );

  return (
    <Box sx={styles.row}>
      <Autocomplete
        getOptionLabel={carrierLabel}
        isOptionEqualToValue={isSameUuid}
        loading={carriers.loading}
        options={carrierOptions}
        renderInput={renderCarrierInput}
        value={carrier}
        onChange={handleCarrierChange}
      />
      <Autocomplete
        disabled={carrierUuid === null}
        filterOptions={keepServerResults}
        getOptionLabel={customerOptionLabel}
        isOptionEqualToValue={isSameUuid}
        loading={customers.loading}
        noOptionsText="No customer of this carrier matches."
        options={customerOptions}
        renderInput={renderCustomerInput}
        value={customer}
        onChange={handleCustomerChange}
        onInputChange={handleCustomerInput}
      />
    </Box>
  );
};

export default ConnectionPickers;
